import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, Button } from '../components/ui';
import { getProducts } from '../services/productService';
import { getCategories } from '../services/categoryService';
import { getSliders } from '../services/sliderService';
import { useToast } from '../components/ui/ToastProvider';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalSliders: 0
  });
  const [loading, setLoading] = useState(true);

  // Load dashboard statistics from API
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        
        // Fetch products, categories, and sliders in parallel
        const [products, categories, sliders] = await Promise.all([
          getProducts().catch(() => []), // Return empty array on error
          getCategories().catch(() => []), // Return empty array on error
          getSliders().catch(() => []) // Return empty array on error
        ]);

        setStats({
          totalProducts: products.length,
          totalCategories: categories.length,
          totalSliders: sliders.length
        });
      } catch (error: any) {
        console.error('Failed to load dashboard stats:', error);
        addToast({
          title: 'Error',
          message: 'Failed to load dashboard statistics. Please try again.',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [addToast]);

  const statsCards = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      ),
      label: t('dashboard.totalProducts'),
      value: loading ? '...' : stats.totalProducts,
      // change: '+12%',
      // changeType: 'positive' as const,
      color: 'coral'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 5.16-1 9-5.45 9-11V7z" />
          <path d="M8 11l2 2 4-4" />
        </svg>
      ),
      label: t('dashboard.totalCategories'),
      value: loading ? '...' : stats.totalCategories,
      // change: '+2',
      // changeType: 'positive' as const,
      color: 'orange'
    },
    // {
    //   icon: (
    //     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    //       <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    //       <polyline points="14,2 14,8 20,8" />
    //       <line x1="16" y1="13" x2="8" y2="13" />
    //       <line x1="16" y1="17" x2="8" y2="17" />
    //       <polyline points="10,9 9,9 8,9" />
    //     </svg>
    //   ),
    //   label: t('dashboard.totalPages'),
    //   value: mockStats.totalPages,
    //   change: '0',
    //   changeType: 'neutral' as const,
    //   color: 'amber'
    // },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      label: t('dashboard.totalSliders'),
      value: loading ? '...' : stats.totalSliders,
      // change: '+5',
      // changeType: 'positive' as const,
      color: 'teal'
    }
  ];

  const quickActions = [
    {
      title: t('products.addProduct'),
      description: 'Add a new product to your catalog',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      ),
      link: '/products',
      color: 'coral'
    },
    {
      title: t('categories.addCategory'),
      description: 'Create a new product category',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 5.16-1 9-5.45 9-11V7z" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      ),
      link: '/categories',
      color: 'orange'
    },
    {
      title: t('sliders.addSlider'),
      description: 'Create a new slider for homepage',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      link: '/sliders',
      color: 'amber'
    },
    // {
    //   title: t('pages.addPage'),
    //   description: 'Create a new content page',
    //   icon: (
    //     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    //       <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    //       <polyline points="14,2 14,8 20,8" />
    //       <line x1="12" y1="11" x2="12" y2="17" />
    //       <line x1="9" y1="14" x2="15" y2="14" />
    //     </svg>
    //   ),
    //   link: '/pages/add',
    //   color: 'amber'
    // },
    // {
    //   title: t('messages.title'),
    //   description: 'Check customer messages',
    //   icon: (
    //     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    //       <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    //       <polyline points="22,6 12,13 2,6" />
    //     </svg>
    //   ),
    //   link: '/messages',
    //   color: 'teal'
    // }
  ];

  // const recentActivity = [
  //   {
  //     id: 1,
  //     type: 'product',
  //     title: 'Added new product "Vitamin D3"',
  //     time: '2 hours ago',
  //     icon: (
  //       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
  //         <line x1="12" y1="5" x2="12" y2="19" />
  //         <line x1="5" y1="12" x2="19" y2="12" />
  //       </svg>
  //     )
  //   },
  //   {
  //     id: 2,
  //     type: 'message',
  //     title: 'New message from customer',
  //     time: '4 hours ago',
  //     icon: (
  //       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
  //         <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
  //         <polyline points="22,6 12,13 2,6" />
  //       </svg>
  //     )
  //   },
  //   {
  //     id: 3,
  //     type: 'page',
  //     title: 'Updated "About Us" page',
  //     time: '1 day ago',
  //     icon: (
  //       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
  //         <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
  //         <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  //       </svg>
  //     )
  //   },
  //   {
  //     id: 4,
  //     type: 'category',
  //     title: 'Added "Supplements" category',
  //     time: '2 days ago',
  //     icon: (
  //       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
  //         <path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 5.16-1 9-5.45 9-11V7z" />
  //       </svg>
  //     )
  //   }
  // ];

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div className="dashboard__title-section">
          <h1 className="dashboard__title">{t('dashboard.title')}</h1>
          <p className="dashboard__subtitle">{t('dashboard.welcome')}</p>
        </div>
        {/* <div className="dashboard__actions">
          <Button variant="secondary" size="sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v5h5" />
              <path d="M3 8a9 9 0 0 1 9-9 9 9 0 0 1 9 9" />
              <path d="M21 21v-5h-5" />
              <path d="M21 16a9 9 0 0 1-9 9 9 9 0 0 1-9-9" />
            </svg>
            {t('common.refresh')}
          </Button>
        </div> */}
      </div>

      <div className="dashboard__stats">
        {statsCards.map((stat, index) => (
          <Card key={index} className={`dashboard__stat-card dashboard__stat-card--${stat.color}`} padding="lg">
            <div className="dashboard__stat-icon">
              {stat.icon}
            </div>
            <div className="dashboard__stat-content">
              <div className="dashboard__stat-value">{stat.value}</div>
              <div className="dashboard__stat-label">{stat.label}</div>
              {/* <div className={`dashboard__stat-change dashboard__stat-change--${stat.changeType}`}>
                {stat.changeType === 'positive' && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" />
                    <polyline points="17,6 23,6 23,12" />
                  </svg>
                )}
                {stat.change}
              </div> */}
            </div>
          </Card>
        ))}
      </div>

      <div className="dashboard__content">
        <div className="dashboard__section">
          <Card padding="lg">
            <div className="dashboard__section-header">
              <h2 className="dashboard__section-title">{t('dashboard.quickActions')}</h2>
            </div>
            <div className="dashboard__quick-actions">
              {quickActions.map((action, index) => (
                <Link key={index} to={action.link} className="dashboard__quick-action">
                  <div className={`dashboard__quick-action-icon dashboard__quick-action-icon--${action.color}`}>
                    {action.icon}
                  </div>
                  <div className="dashboard__quick-action-content">
                    <h3 className="dashboard__quick-action-title">{action.title}</h3>
                    <p className="dashboard__quick-action-description">{action.description}</p>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="dashboard__quick-action-arrow">
                    <line x1="7" y1="17" x2="17" y2="7" />
                    <polyline points="7,7 17,7 17,17" />
                  </svg>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        <div className="dashboard__section">
          {/* <Card padding="lg">
            <div className="dashboard__section-header">
              <h2 className="dashboard__section-title">{t('dashboard.recentActivity')}</h2>
              <Button variant="ghost" size="sm">
                {t('common.view')} All
              </Button>
            </div>
            <div className="dashboard__activity">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="dashboard__activity-item">
                  <div className="dashboard__activity-icon">
                    {activity.icon}
                  </div>
                  <div className="dashboard__activity-content">
                    <p className="dashboard__activity-title">{activity.title}</p>
                    <span className="dashboard__activity-time">{activity.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card> */}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;